import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

function stringOrNull(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function stringId(value) {
  return typeof value === 'string' ? value : '';
}

function count(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized >= 0 ? normalized : 0;
}

function timestampOrNull(value) {
  if (typeof value !== 'string' && typeof value !== 'number' && !(value instanceof Date)) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}

function normalizeAddress(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const address = stringOrNull(value.address);
  if (!address) return null;
  return {
    name: stringOrNull(value.name),
    address,
  };
}

function normalizeMessage(value) {
  const direction = typeof value?.direction === 'string'
    ? value.direction.trim().toLowerCase()
    : '';
  return {
    id: stringId(value?.id),
    direction: ['inbound', 'outbound'].includes(direction) ? direction : 'unknown',
    sender: normalizeAddress(value?.sender),
    recipients: Array.isArray(value?.recipients)
      ? value.recipients.map(normalizeAddress).filter(Boolean)
      : [],
    subject: stringOrNull(value?.subject),
    body: typeof value?.body === 'string' ? value.body : '',
    bodyTruncated: value?.bodyTruncated === true,
    metadataTruncated: value?.metadataTruncated === true,
    recipientsTruncated: value?.recipientsTruncated === true,
    sentAt: timestampOrNull(value?.sentAt),
  };
}

function normalizeThread(value) {
  return {
    id: stringId(value?.id),
    propertyName: stringOrNull(value?.propertyName),
    propertyAddress: stringOrNull(value?.propertyAddress),
    subject: stringOrNull(value?.subject),
    status: stringOrNull(value?.status) || 'unknown',
    updatedAt: timestampOrNull(value?.updatedAt),
    messageCount: count(value?.messageCount),
    messagesTruncated: value?.messagesTruncated === true,
    responseBudgetTruncated: value?.responseBudgetTruncated === true,
    messagesLoaded: value?.messagesLoaded === true,
    messages: Array.isArray(value?.messages) ? value.messages.map(normalizeMessage) : [],
    metadataTruncated: value?.metadataTruncated === true,
  };
}

function normalizeCampaign(value) {
  return {
    id: stringId(value?.id),
    name: stringOrNull(value?.name),
    status: stringOrNull(value?.status) || 'unknown',
    archived: value?.archived === true,
    lastActivityAt: timestampOrNull(value?.lastActivityAt),
    metadataTruncated: value?.metadataTruncated === true,
  };
}

function normalizeDraft(value) {
  return {
    id: stringId(value?.id),
    threadId: stringOrNull(value?.threadId),
    recipient: stringOrNull(value?.recipient),
    subject: stringOrNull(value?.subject),
    body: typeof value?.body === 'string' ? value.body : '',
    bodyTruncated: value?.bodyTruncated === true,
    type: stringOrNull(value?.type) || 'unknown',
    status: stringOrNull(value?.status) || 'unknown',
    createdAt: timestampOrNull(value?.createdAt),
    metadataTruncated: value?.metadataTruncated === true,
  };
}

export function normalizeAdminConversationReview(data) {
  return {
    campaigns: Array.isArray(data?.campaigns) ? data.campaigns.map(normalizeCampaign) : [],
    threads: Array.isArray(data?.threads) ? data.threads.map(normalizeThread) : [],
    drafts: Array.isArray(data?.drafts) ? data.drafts.map(normalizeDraft) : [],
    truncation: {
      campaigns: data?.truncation?.campaigns === true,
      threads: data?.truncation?.threads === true,
      messageThreads: Array.isArray(data?.truncation?.messageThreads)
        ? data.truncation.messageThreads.filter(value => typeof value === 'string' && value)
        : [],
      drafts: data?.truncation?.drafts === true,
      contentBodies: {
        messages: count(data?.truncation?.contentBodies?.messages),
        drafts: count(data?.truncation?.contentBodies?.drafts),
      },
      metadata: {
        records: count(data?.truncation?.metadata?.records),
        recipientLists: count(data?.truncation?.metadata?.recipientLists),
      },
    },
  };
}

export async function requestAdminConversationReview({
  firebaseFunctions = functions,
  targetUid,
  clientId,
  threadId,
} = {}) {
  const payload = { targetUid };
  if (clientId !== undefined && clientId !== null) payload.clientId = clientId;
  if (threadId !== undefined && threadId !== null) payload.threadId = threadId;
  const callable = httpsCallable(firebaseFunctions, 'getAdminCampaignConversations');
  const response = await callable(payload);
  return normalizeAdminConversationReview(response?.data);
}
